import React from 'react';
import anime from 'animejs';
import classNames from 'classnames';
import { CupIcon, InfoIcon, AnimeEasingOptions } from 'twain-sport-fe-mono';
import { translationStore } from 'store/Translations';
import { MESSAGE_ANIMATION_DURATION } from 'constants/constants';
import { IMessage } from 'store/GameMessage/interfaces';
import { MessageType } from 'store/GameMessage/enums/MessageType';
import { useNextMessage } from './hooks/useNextMessage';
import { useGameMessagePosition } from './hooks/useGameMessagePosition';
import classes from './GameMessage.scss';

const getMessageIcon = {
    [MessageType.WIN]: CupIcon,
    [MessageType.INFO]: InfoIcon,
    [MessageType.WARNING]: InfoIcon,
};

interface IProps {
    isColumnLayout: boolean;
    dynamicHeight: boolean;
}

export const GameMessage: React.FC<IProps> = (props) => {
    const t = translationStore.useTranslate();
    const nextMessage = useNextMessage();
    const containerRef = React.useRef<HTMLDivElement>(null);

    const [message, setMessage] = React.useState<IMessage>(null);
    const top = useGameMessagePosition(props.dynamicHeight, containerRef);

    React.useEffect(() => {
        const timeline = anime.timeline({
            duration: MESSAGE_ANIMATION_DURATION,
            easing: AnimeEasingOptions.EASE_IN_OUT_SINE,
            targets: containerRef.current,
        });

        if (nextMessage && !message) {
            // show first message
            setMessage(nextMessage);

            timeline.add({
                opacity: [0, 1],
            });
        } else if (nextMessage) {
            // hide first message and show next message
            timeline
                .add({
                    opacity: [1, 0],
                    complete: () => {
                        setMessage(nextMessage);
                    },
                })
                .add({
                    opacity: [0, 1],
                });
        } else if (message) {
            // hide last message
            timeline.add({
                opacity: [1, 0],
                complete: () => {
                    setMessage(null);
                },
            });
        }
    }, [nextMessage]);

    const MessageIcon = getMessageIcon[message?.type];

    return (
        <div
            ref={containerRef}
            className={classNames(classes.container, {
                [classes.dynamicHeight]: props.dynamicHeight,
                [classes.columnLayout]: props.isColumnLayout,
            })}
            style={props.dynamicHeight ? { top: `${top}px` } : undefined}
        >
            {!!message && (
                <div className={classNames(classes.message, classes[message.type])}>
                    {MessageIcon && <MessageIcon className={classes.icon} />}
                    <span>{t.string(message.text)}</span>
                    {!!message.amount && <span className={classes.amount}>{message.amount}</span>}
                </div>
            )}
        </div>
    );
};
